import {
	BADGE_CATALOG,
	BADGE_ORDER,
	earnedCount,
	loadLocalBadges,
	markLocalBadges,
	nextBadgeId,
	type BadgeDef,
	type BadgeId,
} from "@/lib/badges";
import { useMyAgent, useMyApiKeys } from "@/hooks/useAgentGateway";
import { useUserVaults } from "@/hooks/useUserVaults";
import { useAuth } from "@/lib/auth";
import { useMemo, useSyncExternalStore } from "react";
import { toast } from "sonner";

const listeners = new Set<() => void>();
function subscribe(cb: () => void) {
	listeners.add(cb);
	return () => listeners.delete(cb);
}
function emit() {
	for (const l of listeners) l();
}

export function markBadgeEarned(
	principal: string,
	id: BadgeId | BadgeId[],
	opts?: { silent?: boolean },
) {
	if (!principal) return;
	const before = loadLocalBadges(principal);
	const ids = Array.isArray(id) ? id : [id];
	const newly = ids.filter((b) => !before.has(b));
	if (newly.length === 0) return;
	markLocalBadges(principal, newly);
	emit();
	if (opts?.silent) return;
	for (const badgeId of newly) {
		const def = BADGE_CATALOG[badgeId];
		if (def) toast.success(`Badge unlocked: ${def.title}`, { duration: 3500 });
	}
}

export type BadgeView = BadgeDef & { state: "locked" | "available" | "earned" };

export function useBadgeProgress() {
	const { isAuthenticated, principal, balances } = useAuth();
	const { data: vaults = [] } = useUserVaults();
	const { data: agent } = useMyAgent();
	const { data: keys = [] } = useMyApiKeys();

	const localVersion = useSyncExternalStore(
		subscribe,
		() => {
			try {
				return localStorage.getItem(`motokomaker.v2.badges.${principal ?? ""}`) ?? "";
			} catch {
				return "";
			}
		},
		() => "",
	);

	const earned = useMemo(() => {
		void localVersion;
		const p = principal ?? "";
		const set = loadLocalBadges(p);
		// Auto-detect from chain/session signals
		if (isAuthenticated && p) {
			const auto: BadgeId[] = ["connect_ii"];
			const liveVaults = vaults.filter((v) => v.source === "live");
			if (liveVaults.length > 0) auto.push("create_vault");
			if (
				balances.USDM > 0 ||
				liveVaults.some((v) => (v.debtUsdm ?? 0) > 0)
			) {
				auto.push("mint_usdm");
			}
			if (agent) auto.push("agent_type_f");
			if (keys.some((k) => !k.revoked)) auto.push("agent_type_a");
			const missing = auto.filter((id) => !set.has(id));
			if (missing.length) {
				markLocalBadges(p, missing);
			}
			return loadLocalBadges(p);
		}
		return set;
	}, [
		localVersion,
		principal,
		isAuthenticated,
		vaults,
		balances.USDM,
		agent,
		keys,
	]);

	const level = earnedCount(earned);
	const total = BADGE_ORDER.length;
	const nextId = nextBadgeId(earned);
	const next = nextId ? BADGE_CATALOG[nextId] : null;

	const badges: BadgeView[] = BADGE_ORDER.map((id) => {
		const def = BADGE_CATALOG[id];
		const isEarned = earned.has(id);
		const isNext = nextId === id;
		return {
			...def,
			state: isEarned ? "earned" : isNext ? "available" : "locked",
		};
	});

	return { badges, level, total, next, earned };
}
